import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MobileTailwind from "./MobileTailwind";
import BottomNav from "./components/BottomNav";
import VegIcon from "./components/VegIcon";
import TextInput from "./components/TextInput";
import WebService from "../Services/WebService";
import { getEncryptedSessionData, setEncryptedSessionData } from "../Services/SessionStorageService";
import MobileLoader from "../components/MobileLoader/MobileLoader";
import SubHeader from "./components/SubHeader";
import DishCard, { Dish } from "./components/DishCard";
import EditDishModal from "./components/EditDishModal";
import CartStrip from "./components/CartStrip";
import { useCart } from "./CartContext";
import HomeHeader from "./components/HomeHeader";
import { getPosOrderCacheKey } from "./Tables";

const CATEGORY_IMAGE_MAP: Record<string, string> = {
  breakfast: "/mobile-assets/menu-breakfast.webp",
  lunch: "/mobile-assets/menu-lunch.webp",
  dinner: "/mobile-assets/menu-dinner.webp",
  beverage: "/mobile-assets/menu-beverages.webp",
  beverages: "/mobile-assets/menu-beverages.webp",
  dessert: "/mobile-assets/menu-desserts.webp",
  desserts: "/mobile-assets/menu-desserts.webp",
  drink: "/mobile-assets/menu-drinks.webp",
  drinks: "/mobile-assets/menu-drinks.webp",
};

const getCategoryImage = (name: string): string => {
  const key = name.toLowerCase().trim();
  for (const k of Object.keys(CATEGORY_IMAGE_MAP)) {
    if (key.includes(k)) return CATEGORY_IMAGE_MAP[k];
  }
  return "/mobile-assets/menu-lunch.webp";
};

const getCategoryBgColor = (name: string): string => {
  const n = name.toLowerCase();
  if (n.includes("soup")) return "#FFF5F2"; // light orange/peach
  if (n.includes("salad")) return "#F0FDF4"; // light green
  if (n.includes("starter")) return "#EFF6FF"; // light blue
  if (n.includes("roll")) return "#FFF7ED"; // peach
  if (n.includes("course")) return "#FFF5F2"; // peach
  if (n.includes("rice") || n.includes("biryani")) return "#FEFCE8"; // light yellow
  if (n.includes("bread") || n.includes("roti")) return "#ECFDF5"; // light teal
  if (n.includes("beverage")) return "#FFFBEB"; // light yellow
  if (n.includes("dessert")) return "#FAF5FF"; // light purple
  if (n.includes("shake")) return "#FDF2F8"; // light pink
  return "#FFF7ED";
};

const getCategoryCountText = (cId: string, allItems: Dish[]): string => {
  const count = allItems.filter((item) => String(item.category_id) === String(cId)).length;
  return `${count} ${count === 1 ? "Item" : "Items"}`;
};

const getDishImage = (name: string): string => {
  const n = name.toLowerCase();
  if (n.includes("paneer")) return "https://images.unsplash.com/photo-1631452180519-c014fe946bc7?w=150&auto=format&fit=crop&q=60";
  if (n.includes("soup")) return "https://images.unsplash.com/photo-1547592180-85f173990554?w=150&auto=format&fit=crop&q=60";
  if (n.includes("biryani") || n.includes("rice")) return "https://images.unsplash.com/photo-1633945274405-b6c8069047b0?w=150&auto=format&fit=crop&q=60";
  if (n.includes("chicken")) return "https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?w=150&auto=format&fit=crop&q=60";
  if (n.includes("bread") || n.includes("roti") || n.includes("naan")) return "https://images.unsplash.com/photo-1589301760014-d929f3979dbc?w=150&auto=format&fit=crop&q=60";
  if (n.includes("noodle") || n.includes("noodles")) return "https://images.unsplash.com/photo-1585032226651-759b368d7246?w=150&auto=format&fit=crop&q=60";
  if (n.includes("starter") || n.includes("sixty five") || n.includes("65") || n.includes("kebab") || n.includes("tikka")) return "https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=150&auto=format&fit=crop&q=60";
  return "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=150&auto=format&fit=crop&q=60";
};

const getCategoryOrder = (name: string): number => {
  const cleanName = name.toLowerCase().trim();

  if (cleanName === "soup" || cleanName === "soups") return 0;
  if (cleanName === "munchies" || cleanName === "munchie") return 1;
  if (cleanName === "salad" || cleanName === "salads") return 2;
  if (cleanName === "veg starter" || cleanName === "veg starters") return 3;
  if (cleanName === "paneer starter" || cleanName === "paneer starters") return 4;
  if (cleanName === "rolls" || cleanName === "roll") return 5;
  if (cleanName === "kebab" || cleanName === "kebabs") return 6;
  if (cleanName === "tikka" || cleanName === "tikkas") return 7;
  if (cleanName === "noodles" || cleanName === "noodle") return 8;
  if (cleanName === "veg course" || cleanName === "veg courses") return 9;
  if (cleanName === "paneer course" || cleanName === "paneer courses") return 10;
  if (cleanName === "kofta" || cleanName === "koftas") return 11;
  if (cleanName === "curry" || cleanName === "curries") return 12;
  if (cleanName === "dal" || cleanName === "dals" || cleanName === "daal") return 13;
  if (cleanName === "rice" || cleanName === "rices") return 14;
  if (cleanName === "curd/raita" || cleanName === "curd" || cleanName === "raita" || cleanName === "curd / raita" || cleanName === "curd/ raita") return 15;
  if (cleanName === "indian breads" || cleanName === "indian bread" || cleanName === "breads" || cleanName === "bread") return 16;
  if (cleanName === "platter" || cleanName === "platters") return 17;
  if (cleanName === "mocktails" || cleanName === "mocktail") return 18;
  if (cleanName === "shakes" || cleanName === "shake") return 19;

  if (cleanName.includes("soup")) return 0;
  if (cleanName.includes("munch")) return 1;
  if (cleanName.includes("salad")) return 2;
  if (cleanName.includes("veg starter")) return 3;
  if (cleanName.includes("paneer starter")) return 4;
  if (cleanName.includes("roll")) return 5;
  if (cleanName.includes("kebab")) return 6;
  if (cleanName.includes("tikka")) return 7;
  if (cleanName.includes("noodle")) return 8;
  if (cleanName.includes("veg course")) return 9;
  if (cleanName.includes("paneer course")) return 10;
  if (cleanName.includes("kofta")) return 11;
  if (cleanName.includes("curry") || cleanName.includes("curries")) return 12;
  if (cleanName.includes("dal") || cleanName.includes("daal")) return 13;
  if (cleanName.includes("rice")) return 14;
  if (cleanName.includes("curd") || cleanName.includes("raita")) return 15;
  if (cleanName.includes("bread")) return 16;
  if (cleanName.includes("platter")) return 17;
  if (cleanName.includes("mocktail")) return 18;
  if (cleanName.includes("shake")) return 19;

  return 1000;
};

interface Category {
  id: string;
  name: string;
}

const isSpecialItem = (item: any) =>
  item.is_special === 1 || item.is_special === true || item.is_special === "1";

const isTodaySpecialItem = (item: any) =>
  item.today_special === 1 || item.today_special === true || item.today_special === "1";

const mapApiItemToDish = (item: any): Dish => {
  let type: "veg" | "nonveg" = "veg";
  if (item.food_type) {
    type = String(item.food_type).toLowerCase().includes("non") ? "nonveg" : "veg";
  } else if (item.is_veg !== undefined) {
    type = item.is_veg ? "veg" : "nonveg";
  } else if (item.type) {
    type = String(item.type).toLowerCase().includes("non") ? "nonveg" : "veg";
  } else if (item.choice) {
    type = String(item.choice).toLowerCase().includes("non") ? "nonveg" : "veg";
  }

  const availableQty = Number(item.available_quantity ?? -1);
  const dailyStock = Number(item.daily_stock ?? 0);
  const isOutOfStock = isSpecialItem(item) && availableQty === 0 && dailyStock > 0;

  return {
    id: String(item.id),
    name: item.name || item.item_name || item.menu_name || "",
    code: item.custom_code ?? item.code ?? item.item_code ?? 0,
    price: Number(item.price ?? item.gross_price ?? 0),
    gross_price: Number(item.gross_price ?? item.price ?? 0),
    type,
    isOutOfStock,
    category_id: String(item.server_category_id || item.category_id || ""),
    today_special: isTodaySpecialItem(item),
  };
};

const keyFor = (d: Dish) => `${d.id}-${d.code}`;

// const GiftIcon = () => (
//   <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
//     <rect x="3" y="8" width="18" height="13" rx="2" stroke="#F15A29" strokeWidth="2" />
//     <path
//       d="M3 12h18M12 8v13M8 8a3 3 0 110-6c1.5 0 2.5 1 4 3 1.5-2 2.5-3 4-3a3 3 0 110 6H8z"
//       stroke="#F15A29"
//       strokeWidth="2"
//       strokeLinecap="round"
//       strokeLinejoin="round"
//     />
//   </svg>
// );

const Menu = () => {
  const navigate = useNavigate();
  const { tableNo } = useParams<{ tableNo: string }>();
  const cart = useCart();
  const [recOpen, setRecOpen] = useState(false);
  const [recMobile, setRecMobile] = useState("");
  const [recName, setRecName] = useState("");
  const [recItemsOpen, setRecItemsOpen] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [specials, setSpecials] = useState<Dish[]>([]);
  const [editingDish, setEditingDish] = useState<Dish | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [allItems, setAllItems] = useState<Dish[]>([]);
  const [activeFilter, setActiveFilter] = useState("Popular");
  const [tableDropdownOpen, setTableDropdownOpen] = useState(false);
  const [allTables, setAllTables] = useState<string[]>([]);
  const showNewCustomer = recMobile.length === 10;

  useEffect(() => {
    if (tableNo && cart.tableNo !== tableNo) cart.setTable(tableNo);
  }, [tableNo, cart]);

  const qtyOf = (d: Dish) => cart.lines.find((l) => l.key === keyFor(d))?.quantity ?? 0;

  // ── SWR (Stale-While-Revalidate) categories load ──
  const getCategories = useCallback(() => {
    const tenantId = localStorage.getItem('tenantId') || 'default';
    const cacheKey = `menu_categories_swr_${tenantId}`;
    const cached = getEncryptedSessionData<Category[]>(cacheKey);
    if (cached) {
      const sortedCached = [...cached].sort((a, b) => {
        const orderA = getCategoryOrder(a.name);
        const orderB = getCategoryOrder(b.name);
        if (orderA !== orderB) return orderA - orderB;
        return a.name.localeCompare(b.name);
      });
      setCategories(sortedCached);
    } else {
      setIsLoading(true);
    }

    WebService.getAPI({ action: "/get-all/menu-categories" })
      .then((response: any) => {
        const cats: Category[] = response?.categories || [];
        const sortedCats = [...cats].sort((a, b) => {
          const orderA = getCategoryOrder(a.name);
          const orderB = getCategoryOrder(b.name);
          if (orderA !== orderB) return orderA - orderB;
          return a.name.localeCompare(b.name);
        });
        setCategories(sortedCats);
        setEncryptedSessionData(cacheKey, sortedCats);
      })
      .catch((err) => console.error("Categories fetch error:", err))
      .finally(() => setIsLoading(false));
  }, []);

  // ── SWR (Stale-While-Revalidate) special menu items load ──
  const getSpecials = useCallback(() => {
    WebService.getAPI({ action: "/get-all/special-menu-items" })
      .then((response: any) => {
        const raw: any[] = response?.menus || [];
        const specialDishes = raw.map(mapApiItemToDish).sort((a, b) => {
          if (a.isOutOfStock && !b.isOutOfStock) return 1;
          if (!a.isOutOfStock && b.isOutOfStock) return -1;
          return 0;
        });
        setSpecials(specialDishes);
      })
      .catch((err) => {
        console.error("Specials fetch error:", err);
        setSpecials([]);
      });
  }, []);

  // ── SWR (Stale-While-Revalidate) all menu items load ──
  const getAllItems = useCallback(() => {
    const tenantId = localStorage.getItem('tenantId') || 'default';
    const cacheKey = `menu_items_swr_${tenantId}`;
    const cached = getEncryptedSessionData<Dish[]>(cacheKey);
    if (cached) {
      setAllItems(cached);
    }

    Promise.all([
      WebService.getAPI({ action: "/get-all/menu-items", body: {} }),
      WebService.getAPI({ action: "/get-all/special-menu-items", body: {} })
    ])
      .then(([resNormal, resSpecial]: any[]) => {
        const rawNormal: any[] = resNormal?.menus || resNormal?.items || [];
        const rawSpecial: any[] = resSpecial?.menus || [];

        const mappedNormal = rawNormal.map(mapApiItemToDish);
        const mappedSpecial = rawSpecial.map(mapApiItemToDish);

        setAllItems([...mappedNormal, ...mappedSpecial]);
        setEncryptedSessionData(cacheKey, mappedNormal);
      })
      .catch((err) => console.error("All items fetch error:", err));
  }, []);

  useEffect(() => {
    getCategories();
    getSpecials();
    getAllItems();
  }, [getCategories, getSpecials, getAllItems]);

  const filteredDishes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    if (/^\d+$/.test(q)) {
      return allItems.filter(
        (d) => String(d.code).trim().toLowerCase() === q
      );
    }
    return allItems.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        String(d.code).trim().toLowerCase() === q
    );
  }, [allItems, search]);

  const todaySpecialItems = useMemo(() => {
    const seen = new Set<string>();
    return allItems.filter((item) => {
      if (!item.today_special) return false;
      const key = keyFor(item);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [allItems]);

  const closeRec = () => {
    setRecOpen(false);
    setRecMobile("");
    setRecName("");
  };

  const handleSaveRec = () => {
    if (!recName.trim()) return;
    setCustomerName(recName.trim());
    setRecOpen(false);
    setRecMobile("");
    setRecName("");
    setRecItemsOpen(true);
  };

  const RECOMMENDED_ITEMS = [
    { name: "Thecha Paratha", code: 220, price: 120, type: "veg" as const },
    { name: "Paneer Tikka", code: 200, price: 100, type: "veg" as const },
    { name: "Chicken Curry", code: 100, price: 220, type: "nonveg" as const },
  ];

  const tenantId = localStorage.getItem("tenantId") || "default";

  const recentlyOrderedItems = useMemo(() => {
    const orders = getEncryptedSessionData<Record<string, any>>(getPosOrderCacheKey(tenantId)) || {};
    const list: Dish[] = [];
    const seen = new Set<string>();

    Object.keys(orders).forEach((tableKey) => {
      const order = orders[tableKey];
      if (order && order.menus) {
        order.menus.forEach((item: any) => {
          const dishId = String(item.menu_id || item.id || "");
          const dishName = item.name || item.item_name || item.menu_name || "";
          if (dishId && !seen.has(dishId)) {
            seen.add(dishId);
            const matched = allItems.find(ai => String(ai.id) === dishId);
            if (matched) {
              list.push(matched);
            } else {
              let type: "veg" | "nonveg" = "veg";
              if (item.type || item.food_type) {
                type = String(item.type || item.food_type).toLowerCase().includes("non") ? "nonveg" : "veg";
              }
              list.push({
                id: dishId,
                name: dishName,
                code: Number(item.code || item.custom_code || 0),
                price: Number(item.gross_price ?? item.price ?? 0),
                gross_price: Number(item.gross_price ?? item.price ?? 0),
                type,
                isOutOfStock: false,
                category_id: String(item.server_category_id || item.category_id || ""),
              });
            }
          }
        });
      }
    });

    if (list.length < 4) {
      allItems.forEach((d) => {
        if (list.length < 4 && !seen.has(String(d.id))) {
          seen.add(String(d.id));
          list.push(d);
        }
      });
    }

    return list.slice(0, 4);
  }, [allItems, tenantId]);

  const categoryNameOf = (item: Dish) => {
    const cat = categories.find((c) => String(c.id) === String(item.category_id));
    return cat ? cat.name : "Popular Dish";
  };

  const isTableOccupied = (t: string): boolean => {
    const orders = getEncryptedSessionData<Record<string, any>>(getPosOrderCacheKey(tenantId)) || {};
    return orders[t]?.menus?.length > 0;
  };

  const handleAddToCart = ({ dish, quantity, note }: { dish: Dish; quantity: number; note: string }) => {
    const k = keyFor(dish);
    const existing = cart.lines.find((l) => l.key === k);
    if (existing) {
      cart.setQuantity(k, quantity);
    } else {
      cart.addItem({ key: k, dish, quantity, note });
    }
    setEditingDish(null);
  };

  // Load all tables for dropdown
  useEffect(() => {
    WebService.getAPI({ action: "/get-all-lounge-list", body: {} })
      .then((response: any) => {
        const lounges: any[] = response?.lounges || [];
        const tablesSet = new Set<string>();
        lounges.forEach((lounge) => {
          let tablesList: any[] = [];
          if (Array.isArray(lounge.lounge_tables)) {
            tablesList = lounge.lounge_tables;
          } else {
            try {
              tablesList = JSON.parse(lounge.lounge_tables);
            } catch {
              tablesList = [];
            }
          }
          if (Array.isArray(tablesList)) {
            tablesList.forEach((t) => {
              const no = typeof t === "object" && t !== null ? t.table_no || t.tableNo || t.id : String(t);
              if (no) tablesSet.add(String(no).trim());
            });
          }
        });
        const sortedTables = Array.from(tablesSet).sort((a, b) => {
          const numA = parseInt(a.replace(/\D/g, ""), 10);
          const numB = parseInt(b.replace(/\D/g, ""), 10);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          return a.localeCompare(b);
        });
        setAllTables(sortedTables);
      })
      .catch((err) => console.error("Error fetching lounge tables:", err));
  }, []);

  const filteredCategories = useMemo(() => {
    let result = categories;
    const f = activeFilter.toLowerCase();
    if (f === "veg") {
      result = categories.filter((c) => {
        const name = c.name.toLowerCase();
        return (
          !name.includes("non veg") &&
          !name.includes("nonveg") &&
          !name.includes("chicken") &&
          !name.includes("mutton") &&
          !name.includes("egg")
        );
      });
    } else if (f === "today's special") {
      result = [];
    } else if (f === "combo") {
      result = [];
    } else if (f === "beverages") {
      result = categories.filter((c) => {
        const name = c.name.toLowerCase();
        return (
          name.includes("beverage") ||
          name.includes("drink") ||
          name.includes("mocktail") ||
          name.includes("shake") ||
          name.includes("cold") ||
          name.includes("hot")
        );
      });
    }

    return result.filter((c) => {
      const count = allItems.filter((item) => String(item.category_id) === String(c.id)).length;
      return count > 0;
    });
  }, [categories, activeFilter, allItems]);

  const sidebarItems = [
    {
      id: "tables", label: "Tables", path: "/mobile/tables", icon: (active: boolean) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? "#FFF" : "rgba(255,255,255,0.6)"} strokeWidth="2">
          <rect x="3" y="3" width="7" height="9" rx="1" />
          <rect x="14" y="3" width="7" height="5" rx="1" />
          <rect x="14" y="12" width="7" height="9" rx="1" />
          <rect x="3" y="16" width="7" height="5" rx="1" />
        </svg>
      )
    },
    {
      id: "menu", label: "Menu", path: tableNo ? `/mobile/menu/${tableNo}` : "/mobile/menu", icon: (active: boolean) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? "#FFF" : "rgba(255,255,255,0.6)"} strokeWidth="2">
          <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z" />
          <path d="M6 6h10M6 10h10M6 14h10" />
        </svg>
      )
    },
    {
      id: "cart", label: "Cart", path: "/mobile/cart", badge: cart.totalItems, icon: (active: boolean) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? "#FFF" : "rgba(255,255,255,0.6)"} strokeWidth="2">
          <circle cx="8" cy="21" r="1" />
          <circle cx="19" cy="21" r="1" />
          <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
        </svg>
      )
    },
    {
      id: "orders", label: "Orders", path: "/mobile/orders", icon: (active: boolean) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? "#FFF" : "rgba(255,255,255,0.6)"} strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18M8 14h8M8 18h5" />
        </svg>
      )
    },
    {
      id: "bills", label: "Bills", path: tableNo ? `/mobile/payment/${tableNo}` : "/mobile/payment", icon: (active: boolean) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? "#FFF" : "rgba(255,255,255,0.6)"} strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      disabled: true
    },
    {
      id: "more", label: "More", path: "/mobile/profile", icon: (active: boolean) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? "#FFF" : "rgba(255,255,255,0.6)"} strokeWidth="2">
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      )
    }
  ];

  return (
    <>
      <MobileTailwind />
      <style>{`
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>

      {/* Tablet view sidebar + category grid (visible only on md screens and above) */}
      <div className="hidden md:flex h-screen w-full bg-[#FAFAFA] overflow-hidden font-inter">
        {/* Sidebar */}
        <div className="w-[100px] shrink-0 bg-[#0F172A] flex flex-col items-center py-6 gap-8 text-white">
          {/* Logo container */}
          <div className="w-[50px] h-[50px] rounded-full bg-white flex items-center justify-center shadow-sm overflow-hidden p-1">
            <img
              src="/mobile-assets/eagle.png"
              alt="Logo"
              className="w-full h-full object-contain"
            />
          </div>

          {/* Navigation items */}
          <nav className="flex-1 flex flex-col gap-6 w-full px-2">
            {sidebarItems.map((item) => {
              const active = item.id === "menu";
              const isDisabled = item.disabled === true;
              return (
                <button
                  key={item.id}
                  disabled={isDisabled}
                  onClick={() => !isDisabled && navigate(item.path)}
                  className={`w-full flex flex-col items-center py-3.5 rounded-[12px] gap-2 transition-colors relative border-0 bg-transparent text-white ${isDisabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer"
                    }`}
                >
                  {active && (
                    <div className="absolute inset-0 bg-[#F15A29] rounded-[12px] -z-10 shadow-md" />
                  )}
                  {item.icon(active)}
                  <span className={`text-[12px] font-sansation font-bold ${active ? "text-white" : "text-slate-400"}`}>
                    {item.label}
                  </span>
                  {item.badge && item.badge > 0 ? (
                    <span className="absolute top-1.5 right-4 bg-red-500 text-white rounded-full text-[10px] w-4 h-4 flex items-center justify-center font-bold">
                      {item.badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 h-screen overflow-y-auto bg-[#F8FAFC] px-8 py-6 flex flex-col">
          {/* Top Header Row */}
          <header className="flex items-center justify-between gap-6 mb-6">
            {/* Table Dropdown Selector */}
            <div className="relative">
              <button
                onClick={() => setTableDropdownOpen(!tableDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-[8px] border border-[#E2E8F0] font-sansation font-bold text-[#1F2937] text-[15px] shadow-sm hover:bg-[#F8FAFC] transition-colors border-0 cursor-pointer"
              >
                Table {tableNo || '40'}
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" className="ml-1">
                  <path d="M1 1l4 4 4-4" stroke="#1F2937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {tableDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setTableDropdownOpen(false)} />
                  <div className="absolute left-0 mt-2 w-[180px] bg-white rounded-[8px] border border-[#E2E8F0] shadow-lg max-h-[300px] overflow-y-auto z-50 py-1">
                    {allTables.map((t) => {
                      const occupied = isTableOccupied(t);
                      const isCurrent = String(tableNo) === String(t);
                      return (
                        <button
                          key={t}
                          disabled={occupied && !isCurrent}
                          onClick={() => {
                            if (occupied && !isCurrent) return;
                            setTableDropdownOpen(false);
                            cart.setTable(t);
                            navigate(`/mobile/menu/${t}`);
                          }}
                          className={`w-full text-left px-4 py-2.5 font-sansation font-bold text-[14px] border-0 bg-transparent flex items-center justify-between ${occupied && !isCurrent
                            ? "text-gray-300 cursor-not-allowed opacity-50"
                            : isCurrent
                              ? "bg-[#F15A29]/10 text-[#F15A29]"
                              : "text-[#475569] hover:bg-[#F15A29]/10 hover:text-[#F15A29] cursor-pointer"
                            }`}
                        >
                          <span>Table {t}</span>
                          {occupied && !isCurrent && (
                            <span className="text-[10px] text-red-500 uppercase tracking-wider font-opensans font-semibold">
                              Occupied
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Search Input Bar */}
            <div className="relative flex-1 max-w-[420px] shadow-sm rounded-[8px]">
              <input
                type="text"
                placeholder="Search dish or category..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-white rounded-[8px] border border-[#E2E8F0] px-10 py-2.5 font-opensans text-[14px] text-[#1F2937] outline-none placeholder:text-[#94A3B8] focus:border-[#F15A29]"
              />
              <svg className="absolute left-3.5 top-1/2 -translate-y-1/2" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>

            {/* Cart Strip Indicator */}
            <button
              onClick={() => navigate("/mobile/cart")}
              className="flex items-center gap-2 px-5 py-2.5 rounded-[8px] text-white font-sansation font-bold text-[14px] uppercase transition-all hover:opacity-90 shadow-md border-0 cursor-pointer"
              style={{ backgroundColor: "#F15A29" }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="8" cy="21" r="1" />
                <circle cx="19" cy="21" r="1" />
                <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
              </svg>
              <span>{cart.totalItems} Items | ₹{cart.total.toFixed(0)}</span>
            </button>
          </header>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {["Popular", "Recent", "Veg", "Today's Special", "Combo", "Beverages"].map((tab) => {
              const active = activeFilter === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveFilter(tab)}
                  className={`px-5 py-2 rounded-full font-sansation font-bold text-[13px] border-0 cursor-pointer shadow-sm transition-all ${active
                    ? "bg-[#F15A29] text-white"
                    : "bg-white text-[#475569] border border-[#E2E8F0] hover:bg-[#F8FAFC]"
                    }`}
                >
                  {tab === "Popular" ? "★ Popular" : tab}
                </button>
              );
            })}
          </div>

          <MobileLoader show={isLoading} />

          {/* Categories Grid or Search Results */}
          <div className="flex-1">
            {search.trim() ? (
              <div className="flex flex-col gap-3">
                {filteredDishes.length === 0 ? (
                  <p className="text-center font-opensans text-[15px] text-[#8F9098] mt-6">
                    No dishes found
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filteredDishes.map((d) => {
                      const k = keyFor(d);
                      return (
                        <DishCard
                          key={k}
                          dish={d}
                          quantity={qtyOf(d)}
                          onAdd={() => !d.isOutOfStock && setEditingDish(d)}
                          onIncrement={() => cart.increment(k)}
                          onDecrement={() => cart.decrement(k)}
                          onEdit={() => !d.isOutOfStock && setEditingDish(d)}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Special Menu Carousel on Tablet with Smooth Slide Snap */}
                {activeFilter !== "Today's Special" && specials.length > 0 && (
                  <div className="mb-6">
                    <h2 className="font-sansation font-bold text-[#0F172A] text-[18px] mb-3">
                      Special Menu
                    </h2>
                    <div
                      className="flex gap-4 overflow-x-auto pb-2 hide-scrollbar snap-x snap-mandatory scroll-smooth"
                      style={{ WebkitOverflowScrolling: "touch" }}
                    >
                      {specials.map((d) => {
                        const k = keyFor(d);
                        return (
                          <div key={k} className="shrink-0 w-[280px] snap-start">
                            <DishCard
                              dish={d}
                              quantity={qtyOf(d)}
                              onAdd={() => !d.isOutOfStock && setEditingDish(d)}
                              onIncrement={() => cart.increment(k)}
                              onDecrement={() => cart.decrement(k)}
                              onEdit={() => !d.isOutOfStock && setEditingDish(d)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {activeFilter === "Today's Special" ? (
                  <>
                    <h2 className="font-sansation font-bold text-[#0F172A] text-[18px] mb-4">
                      Today's Special
                    </h2>
                    {todaySpecialItems.length === 0 ? (
                      <div className="flex items-center justify-center py-10 bg-white rounded-[12px] border border-[#EFF6FF] shadow-sm">
                        <p className="font-sansation font-bold text-[#64748B] text-[16px] text-center">
                          No Today's Special Found
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {todaySpecialItems.map((d) => {
                          const k = keyFor(d);
                          return (
                            <DishCard
                              key={k}
                              dish={d}
                              quantity={qtyOf(d)}
                              onAdd={() => !d.isOutOfStock && setEditingDish(d)}
                              onIncrement={() => cart.increment(k)}
                              onDecrement={() => cart.decrement(k)}
                              onEdit={() => !d.isOutOfStock && setEditingDish(d)}
                            />
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <h2 className="font-sansation font-bold text-[#0F172A] text-[18px] mb-4">
                      {activeFilter === "Popular" ? "Popular Categories" : `${activeFilter} Categories`}
                    </h2>
                    {filteredCategories.length === 0 ? (
                      <div className="flex items-center justify-center py-10 bg-white rounded-[12px] border border-[#EFF6FF] shadow-sm">
                        <p className="font-sansation font-bold text-[#64748B] text-[16px] text-center">
                          No Category Found
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filteredCategories.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() =>
                              navigate(
                                tableNo
                                  ? `/mobile/dishes/${c.id}/${tableNo}`
                                  : `/mobile/dishes/${c.id}`,
                                { state: { categoryName: c.name } }
                              )
                            }
                            className="rounded-[12px] border border-[#EFF6FF] flex items-center gap-4 p-4 py-3 transition-transform hover:scale-[1.02] shadow-sm cursor-pointer border-0"
                            style={{ backgroundColor: getCategoryBgColor(c.name) }}
                          >
                            <div className="w-[58px] h-[58px] rounded-full border-2 border-white overflow-hidden bg-white shrink-0 shadow-sm">
                              <img
                                src={getCategoryImage(c.name)}
                                alt={c.name}
                                className="w-full h-full object-cover rounded-full"
                              />
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <h3 className="font-sansation font-bold text-[#0F172A] text-[15px] lg:text-[16px] tracking-wide uppercase truncate">
                                {c.name}
                              </h3>
                              <span className="font-opensans text-[13px] text-[#64748B] mt-0.5 block">
                                {getCategoryCountText(c.id, allItems)}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Recently Ordered Row */}
                {activeFilter !== "Today's Special" && recentlyOrderedItems.length > 0 && (
                  <>
                    <div className="flex justify-between items-center mb-3 mt-6">
                      <h2 className="font-sansation font-bold text-[#0F172A] text-[18px]">
                        Recently Ordered
                      </h2>
                      <button onClick={() => navigate("/mobile/orders")} className="font-sansation font-bold text-[#F15A29] text-[14px] border-0 bg-transparent cursor-pointer">
                        View All
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {recentlyOrderedItems.map((item) => (
                        <div key={item.id} className="bg-white rounded-[10px] border border-[#E2E8F0] p-3 flex items-center gap-3 shadow-sm">
                          <div className="w-[44px] h-[44px] rounded-[6px] overflow-hidden shrink-0">
                            <img src={getDishImage(item.name)} alt="" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-sansation font-bold text-[#0F172A] text-[14px] truncate">{item.name}</h4>
                            <span className="font-opensans text-[11px] text-[#64748B]">{categoryNameOf(item)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile view container (visible only on mobile screens) */}
      <div id="mobile-root" className="h-screen w-full bg-black flex justify-center overflow-hidden md:hidden">
        <div className="relative w-full h-screen overflow-y-auto overflow-x-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/mobile-assets/login-bg.jpg"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/80" />
          </div>

          <div className="relative z-10">
            <SubHeader title="Menu" onBack={() => navigate("/mobile/tables")} />
            <HomeHeader searchValue={search} onSearchChange={setSearch} />

            <MobileLoader show={isLoading} />
            <div className="px-3 md:px-6 pt-4 pb-[120px] flex flex-col gap-3">
              {search.trim() ? (
                <div className="flex flex-col gap-3">
                  {filteredDishes.length === 0 ? (
                    <p className="text-center font-opensans text-[15px] text-[#8F9098] mt-6">
                      No dishes found
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {filteredDishes.map((d) => {
                        const k = keyFor(d);
                        return (
                          <DishCard
                            key={k}
                            dish={d}
                            quantity={qtyOf(d)}
                            onAdd={() => !d.isOutOfStock && setEditingDish(d)}
                            onIncrement={() => cart.increment(k)}
                            onDecrement={() => cart.decrement(k)}
                            onEdit={() => !d.isOutOfStock && setEditingDish(d)}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Special Menu — horizontal scroll */}
                  {specials.length > 0 && (
                    <div className="mb-1">
                      <h2 className="font-sansation font-bold text-white text-[16px] mb-2">
                        Special Menu
                      </h2>
                      <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
                        {specials.map((d) => {
                          const k = keyFor(d);
                          return (
                            <div key={k} className="shrink-0 w-[280px]">
                              <DishCard
                                dish={d}
                                quantity={qtyOf(d)}
                                onAdd={() => !d.isOutOfStock && setEditingDish(d)}
                                onIncrement={() => cart.increment(k)}
                                onDecrement={() => cart.decrement(k)}
                                onEdit={() => !d.isOutOfStock && setEditingDish(d)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {categories
                      .filter((c) => {
                        const count = allItems.filter((item) => String(item.category_id) === String(c.id)).length;
                        return count > 0;
                      })
                      .map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() =>
                            navigate(
                              tableNo
                                ? `/mobile/dishes/${c.id}/${tableNo}`
                                : `/mobile/dishes/${c.id}`,
                              { state: { categoryName: c.name } }
                            )
                          }
                          className="bg-white rounded-[6px] border border-[#E6EEFF] flex items-center gap-3 p-3 py-2 border-0 cursor-pointer"
                        >
                          <div className="w-[52px] h-[52px] rounded-full border-2 border-[#EFF4FF] overflow-hidden bg-white shrink-0">
                            <img
                              src={getCategoryImage(c.name)}
                              alt={c.name}
                              className="w-full h-full object-cover rounded-full"
                            />
                          </div>
                          <span className="flex-1 text-left font-sansation font-bold text-[#111] text-[16px]">
                            {c.name}
                          </span>
                          <svg width="8" height="14" viewBox="0 0 8 14" fill="none" className="shrink-0">
                            <path
                              d="M1 1l6 6-6 6"
                              stroke="#F15A29"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      ))}
                  </div>
                </>
              )}
            </div>

            {cart.totalItems > 0 ? (
              <CartStrip
                tableNo={cart.tableNo ?? tableNo}
                itemCount={cart.totalItems}
                onViewCart={() => navigate("/mobile/cart")}
                onClose={() => cart.clear()}
              />
            ) : (
              <BottomNav active="menu" tableNo={tableNo} />
            )}

            {/* Recommended Items Sheet */}
            {recItemsOpen && (
              <div
                className="fixed inset-0 z-[70] flex items-end justify-center"
                onClick={() => setRecItemsOpen(false)}
              >
                <div
                  className="absolute inset-0 backdrop-blur-sm"
                  style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
                />
                <div
                  className="relative w-full max-w-[430px] bg-white rounded-t-[20px] pt-2 pb-4 px-2 max-h-[90vh] flex flex-col rec-items-sheet"
                  onClick={(e) => e.stopPropagation()}
                >
                  <style>{`
                    @keyframes recItemsSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
                    .rec-items-sheet { animation: recItemsSlideUp 240ms ease-out; }
                  `}</style>
                  <div className="flex justify-center pb-2">
                    <span className="block w-10 h-1 rounded-full bg-[#E8E9F1]" />
                  </div>
                  <div className="px-2 flex items-center justify-between">
                    <h2 className="font-sansation font-bold text-[#111] text-[16px] leading-none">
                      Recommended Items
                    </h2>
                    <button
                      type="button"
                      aria-label="Close"
                      onClick={() => setRecItemsOpen(false)}
                      className="w-6 h-6 flex items-center justify-center bg-transparent border-0 p-0"
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                          d="M1 1l12 12M13 1L1 13"
                          stroke="#111"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>

                  <p className="mt-2 px-2 font-opensans font-bold text-[#111] text-[20px] leading-tight">
                    Welcome, {customerName || "Guest"}!
                  </p>

                  <div className="mt-4 flex flex-col gap-3 max-h-[400px] overflow-y-auto px-2">
                    {RECOMMENDED_ITEMS.map((it) => (
                      <article
                        key={it.code}
                        className="bg-white rounded-[6px] border border-[#E6EEFF] p-3 relative"
                      >
                        <button
                          type="button"
                          aria-label={`Edit ${it.name}`}
                          className="absolute top-2.5 right-2.5 w-6 h-6 flex items-center justify-center bg-transparent border-0"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M4 20h4l10-10-4-4L4 16v4zM14 6l4 4"
                              stroke="#111"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>

                        <div className="flex items-center gap-2 pr-7">
                          <VegIcon type={it.type} size={16} />
                          <h3 className="font-sansation font-bold text-[#111] text-[15px] truncate">
                            {it.name}
                          </h3>
                        </div>

                        <div className="mt-2 flex items-center justify-between gap-2">
                          <div className="flex flex-col min-w-0">
                            <p className="font-opensans uppercase text-[11px] tracking-[0.5px] text-[#8F9098]">
                              Dish Code: {it.code}
                            </p>
                            <p className="mt-0.5 font-sansation font-bold text-[#F15A29] text-[16px]">
                              ₹{it.price.toFixed(2)}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setRecItemsOpen(false);
                              navigate(
                                tableNo
                                  ? `/mobile/dishes/special/${tableNo}`
                                  : `/mobile/dishes/special`,
                                { state: { categoryName: "Special" } }
                              );
                            }}
                            className="shrink-0 h-[34px] rounded-[6px] px-5 inline-flex items-center gap-1 font-sansation font-bold text-[14px] tracking-[-0.28px] uppercase text-white border-0"
                            style={{ backgroundColor: "#F15A29" }}
                          >
                            ADD <span className="text-[16px] leading-none">+</span>
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Recommendation Input Sheet */}
            {recOpen && (
              <div
                className="fixed inset-0 z-[70] flex items-end justify-center"
                onClick={closeRec}
              >
                <div
                  className="absolute inset-0 backdrop-blur-sm"
                  style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
                />
                <div
                  className="relative w-full max-w-[430px] bg-white rounded-t-[20px] pt-2 pb-6 px-4 max-h-[90vh] flex flex-col rec-sheet"
                  onClick={(e) => e.stopPropagation()}
                >
                  <style>{`
                    @keyframes recSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
                    .rec-sheet { animation: recSlideUp 240ms ease-out; }
                  `}</style>
                  <div className="flex justify-center pb-2">
                    <span className="block w-10 h-1 rounded-full bg-[#E8E9F1]" />
                  </div>
                  <div className="flex items-center justify-between">
                    <h2 className="font-sansation font-bold text-[#111] text-[16px] leading-none">
                      Recommendation
                    </h2>
                    <button
                      type="button"
                      aria-label="Close"
                      onClick={closeRec}
                      className="w-6 h-6 flex items-center justify-center bg-transparent border-0 p-0"
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                          d="M1 1l12 12M13 1L1 13"
                          stroke="#111"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>

                  <TextInput
                    containerClassName="mt-[21px]"
                    label="Mobile Number"
                    type="tel"
                    inputMode="numeric"
                    autoFocus
                    value={recMobile}
                    onChange={(e) => setRecMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="Enter mobile number"
                  />

                  {showNewCustomer && (
                    <>
                      <p className="mt-4 font-opensans text-[14px] text-[rgba(17,17,17,0.8)] text-center leading-snug">
                        Ohh! You are new customer. Please enter your name &amp; mobile number to
                        Recommendation
                      </p>

                      <TextInput
                        containerClassName="mt-4"
                        label="Name"
                        type="text"
                        value={recName}
                        onChange={(e) => setRecName(e.target.value)}
                        placeholder="Enter your name"
                      />

                      <button
                        type="button"
                        disabled={!recName.trim()}
                        onClick={handleSaveRec}
                        className="mt-4 w-full h-11 rounded-[6px] font-sansation font-bold text-[14px] tracking-[-0.28px] uppercase text-white border-0 disabled:opacity-60"
                        style={{ backgroundColor: "#F15A29" }}
                      >
                        Save
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <EditDishModal
        open={editingDish !== null}
        dish={editingDish}
        initialQuantity={editingDish ? qtyOf(editingDish) || 1 : 1}
        initialNote={editingDish ? cart.lines.find((l) => l.key === keyFor(editingDish))?.note ?? "" : ""}
        onClose={() => setEditingDish(null)}
        onAddToCart={handleAddToCart}
      />
    </>
  );
};

export default Menu;
